import i18n from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'
import {
	applyLocaleToDocument,
	normalizeLocale,
	persistLocale,
	resolveInitialLocale,
	toBackendLocale,
} from './locale'
import {
	FALLBACK_LOCALE,
	I18N_NAMESPACES,
	SUPPORTED_LOCALES,
	type BackendLocale,
	type SupportedLocale,
} from './types'

export const initI18n = async (): Promise<typeof i18n> => {
	if (i18n.isInitialized) {
		return i18n
	}

	const initialLocale = await resolveInitialLocale()

	await i18n
		.use(initReactI18next)
		.use(
			resourcesToBackend((language: string, namespace: string) => {
				return import(`./locales/${language}/${namespace}.json`)
			})
		)
		.init({
			lng: initialLocale,
			fallbackLng: FALLBACK_LOCALE,
			supportedLngs: [...SUPPORTED_LOCALES],
			ns: [...I18N_NAMESPACES],
			defaultNS: 'common',
			load: 'languageOnly',
			interpolation: {
				escapeValue: false,
			},
			react: {
				useSuspense: false,
			},
		})

	applyLocaleToDocument(initialLocale)

	i18n.on('languageChanged', (language) => {
		const locale = normalizeLocale(language)
		applyLocaleToDocument(locale)
		void persistLocale(locale)
	})

	return i18n
}

export const getCurrentLocale = (): SupportedLocale => {
	return normalizeLocale(i18n.resolvedLanguage || i18n.language)
}

export const getCurrentBackendLocale = (): BackendLocale => {
	return toBackendLocale(getCurrentLocale())
}

export * from './locale'
export * from './types'
export { i18n }
